import { Link } from "react-router-dom";
import { CustomerItem } from "../../../../mocks/customers";
import type { PaymentFormPayload } from "../../../../lib/salesFlow";
import { ShareWithAnimal } from "./ShareSelectorList";

interface Props {
  customer: CustomerItem;
  selectedShares: ShareWithAnimal[];
  payment: PaymentFormPayload;
  onNewPayment: () => void;
}

function fmt(n: number) {
  return n.toLocaleString("tr-TR", {
    style: "currency",
    currency: "TRY",
    maximumFractionDigits: 0,
  });
}

export default function PaymentSuccess({
  customer,
  selectedShares,
  payment,
  onNewPayment,
}: Props) {
  const uniqueAnimals = Array.from(
    new Map(selectedShares.map((item) => [item.animal.id, item.animal])).values()
  );

  return (
    <div className="flex flex-col items-center py-4 animate-fade-in">
      <div className="w-16 h-16 flex items-center justify-center rounded-full bg-green-100 mb-3">
        <i className="ri-checkbox-circle-fill text-green-500 text-4xl" />
      </div>

      <h2 className="text-lg font-bold text-gray-800 mb-1">Ödeme Kaydedildi</h2>
      <p className="text-sm text-gray-500 mb-6 text-center">
        {customer.adSoyad} adına başarıyla kaydedildi.
      </p>

      <div className="w-full bg-gray-50 rounded-xl p-4 mb-4 space-y-2">
        <div className="flex justify-between text-sm gap-3">
          <span className="text-gray-500">Müşteri</span>
          <span className="font-medium text-gray-800 text-right">
            {customer.adSoyad}
          </span>
        </div>

        <div className="flex justify-between text-sm gap-3">
          <span className="text-gray-500">Telefon</span>
          <span className="font-mono text-gray-700 text-right">
            {customer.telefon}
          </span>
        </div>

        <div className="flex justify-between text-sm gap-3">
          <span className="text-gray-500">Tarih</span>
          <span className="text-gray-700 text-right">{payment.tarih}</span>
        </div>

        {payment.fisNo?.trim() && (
          <div className="flex justify-between text-sm gap-3">
            <span className="text-gray-500">Fiş No</span>
            <span className="font-mono text-gray-700 text-right">
              {payment.fisNo}
            </span>
          </div>
        )}

        <div className="border-t border-gray-200 my-1" />

        <div className="flex justify-between text-sm gap-3">
          <span className="text-gray-500">Tutar</span>
          <span className="font-bold text-green-700 text-right">
            {fmt(payment.tutar)}
          </span>
        </div>

        <div className="flex justify-between text-sm gap-3">
          <span className="text-gray-500">Ödeme Tipi</span>
          <span
            className={`font-medium px-2 py-0.5 rounded-full text-xs ${
              payment.tip === "Nakit"
                ? "bg-green-100 text-green-700"
                : "bg-sky-100 text-sky-700"
            }`}
          >
            {payment.tip}
          </span>
        </div>

        {payment.havaleNo?.trim() && (
          <div className="flex justify-between text-sm gap-3">
            <span className="text-gray-500">Havale No</span>
            <span className="font-mono text-gray-700 text-xs text-right">
              {payment.havaleNo}
            </span>
          </div>
        )}

        {payment.aciklama?.trim() && (
          <div className="flex justify-between text-sm gap-3">
            <span className="text-gray-500">Açıklama</span>
            <span className="text-gray-700 text-xs text-right">
              {payment.aciklama}
            </span>
          </div>
        )}

        <div className="border-t border-gray-200 my-1" />

        <div className="flex justify-between text-sm gap-3">
          <span className="text-gray-500">Hisseler</span>
          <span className="text-gray-700 text-right">
            {selectedShares.map((item) => item.share.hisseNo).join(", ")}
          </span>
        </div>
      </div>

      <div className="w-full space-y-2 mb-4">
        <p className="text-xs text-gray-400 font-medium uppercase tracking-wide">
          Hızlı Erişim
        </p>

        <Link
          to={`/panel/musteriler/${customer.id}`}
          className="flex items-center gap-3 w-full border border-gray-200 rounded-lg px-4 py-2.5 hover:bg-gray-50 transition-colors cursor-pointer"
        >
          <div className="w-7 h-7 flex items-center justify-center">
            <i className="ri-user-line text-gray-500 text-lg" />
          </div>
          <div>
            <p className="text-sm font-medium text-gray-700">Müşteri Detayı</p>
            <p className="text-xs text-gray-400">{customer.adSoyad}</p>
          </div>
          <i className="ri-arrow-right-s-line ml-auto text-gray-400" />
        </Link>

        {uniqueAnimals.map((animal) => (
          <Link
            key={animal.id}
            to={`/panel/hayvanlar/${animal.id}`}
            className="flex items-center gap-3 w-full border border-gray-200 rounded-lg px-4 py-2.5 hover:bg-gray-50 transition-colors cursor-pointer"
          >
            <div className="w-7 h-7 flex items-center justify-center">
              <i className="ri-leaf-line text-gray-500 text-lg" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-700">Hayvan Detayı</p>
              <p className="text-xs text-gray-400 font-mono">{animal.kupeNo}</p>
            </div>
            <i className="ri-arrow-right-s-line ml-auto text-gray-400" />
          </Link>
        ))}
      </div>

      <div className="w-full grid grid-cols-2 gap-2">
        <button
          type="button"
          onClick={onNewPayment}
          className="py-2.5 text-sm font-medium border-2 border-orange-400 text-orange-600 rounded-lg hover:bg-orange-50 transition-colors cursor-pointer whitespace-nowrap"
        >
          Yeni Ödeme Al
        </button>

        <Link
          to="/panel/musteriler"
          className="py-2.5 text-sm font-medium text-center bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors cursor-pointer whitespace-nowrap"
        >
          Müşteri Listesi
        </Link>
      </div>
    </div>
  );
}